<template>
  <div>
    <div class="mt-[-50px] font-Spectral md:mt-[-100px] md:pt-[50px] h1:mt-[-140px] h1:pt-[70px]">
      <MainVisualSection :scroll-to-pick-up="scrollToPickUp" />
      <!-- pb は付けない: 最後の子 ExperientialStaysSection は自前の py-20 を持ち、次の ThemeSection も pt-20 を持つため、
           bg-black6 の下余白を足すと暗い余白が 3 段(240px)積み重なって過大になる。pt-20 のみにして他セクション間と同じ 160px に揃える。 -->
      <section class="bg-black6 pt-20 md:px-4">
        <CategoryCards spacious />
        <PickUpSection v-if="pickUpExperienceSection" :experience-section="pickUpExperienceSection" :on-experience-click="onExperienceClick" />
        <!-- Figma 準拠の並び: PickUp → Recently Viewed → Destinations → Experiential Stays -->
        <RecentlyViewedSection v-if="experienceBrowsingHistory.length > 0" :experiences="experienceBrowsingHistory" :on-experience-click="onExperienceClick" />
        <DestinationsSection
          class="pt-20"
          :destination-list="destinationList"
          :on-click-view-all="onClickDestinationsViewAll"
          :on-destination-click="onDestinationClick"
        />
        <!-- ExperientialStays は画像を useExperientialStays がクライアントのみで取得するため、SSR で描画するとカードが画像なしで先行描画されハイドレーション不整合になる。ClientOnly に閉じる。 -->
        <ClientOnly>
          <ExperientialStaysSection class="mt-20" :experiential-stay-list="experientialStayList" :on-featured-click="onFeaturedClick" />
        </ClientOnly>
      </section>
      <ExperiencesSection
        :en-stories="enStories"
        :experiences-sections="recommendedExperienceSections"
        :on-click-experiences-button="onClickExperiencesButton"
        :on-click-story-view-all="onClickStoryViewAll"
        :on-click-theme-view-all="onClickThemeViewAll"
        :on-experience-click="onExperienceClick"
        :on-featured-click="onFeaturedClick"
        :on-view-all="onViewAll"
        :theme-list="themeList"
      />
      <ContactSection :on-click-contact="onClickContact" :on-click-partner="onClickPartner" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import ContactSection from '@/components/organisms/pages/en/top/ContactSection/ContactSection.vue';
import MainVisualSection from '@/components/organisms/pages/en/top/MainVisualSection/MainVisualSection.vue';
import PickUpSection from '@/components/organisms/pages/en/top/PickUpSection/PickUpSection.vue';
import RecentlyViewedSection from '@/components/organisms/pages/en/top/RecentlyViewedSection/RecentlyViewedSection.vue';
import { useEnStories } from '@/composables/models/useEnStories';
import { useWabunkaExperienceBrowsingHistory } from '@/composables/models/wabunka/query/useWabunkaExperienceBrowsingHistory';
import { useBuildUrl } from '@/composables/utils/useBuildUrl';
import { useFeaturedListQuery } from '@/wabunka-src/common/features/Featured/composables/query/useGetFeaturedListQuery';
import { STORIES_COUNT_FOR_TOP } from '@/wabunka-src/constants/enStories';
import { ICON_FONT_URL } from '@/wabunka-src/constants/iconFontUrl';
import { useTopPageHandlers } from '@/wabunka-src/features/Top/composables/general/useTopPageHandlers';
import { useExperientialStays } from '@/wabunka-src/features/Top/composables/query/useExperientialStays';
import { useTopExperienceSections } from '@/wabunka-src/features/Top/composables/query/useTopExperienceSections';
import CategoryCards from '@/wabunka-src/features/Top/pages/components/CategoryCards/CategoryCards.vue';
import DestinationsSection from '@/wabunka-src/features/Top/pages/components/DestinationsSection/DestinationsSection.vue';
import ExperiencesSection from '@/wabunka-src/features/Top/pages/components/ExperiencesSection/ExperiencesSection.vue';
import ExperientialStaysSection from '@/wabunka-src/features/Top/pages/components/ExperientialStaysSection/ExperientialStaysSection.vue';

const { pickUpExperienceSection, recommendedExperienceSections, curatedStaysSection } = await useTopExperienceSections();
const limit = ref<number>(STORIES_COUNT_FOR_TOP);
const { enStories } = await useEnStories({ limit }, { theme: 'Person' });

const route = useRoute();
const { buildUrlWithEndSlash } = useBuildUrl();

const { experienceBrowsingHistory } = await useWabunkaExperienceBrowsingHistory();

// トップページ共通の GA / ナビゲーション / モーダル操作ハンドラ（Theme 操作も内包）
const {
  scrollToPickUp,
  onClickContact,
  onClickPartner,
  onClickExperiencesButton,
  onClickStoryViewAll,
  onExperienceClick,
  onViewAll,
  onFeaturedClick,
  onDestinationClick,
  onClickDestinationsViewAll,
  onClickThemeViewAll,
} = useTopPageHandlers();

// キーワード検索ポップアップと飛び先を揃えるため destination・theme を取得する。
const { destinationList, themeList } = await useFeaturedListQuery({
  types: ['destination', 'theme'],
});
// 商品データ（タイトル/日程/価格/場所）は useTopExperienceSections 取得済みの Curated_Stays セクション、
// 画像は microCMS en-experiences をスラグ突合して組み立てる（/experienceSections は二重に叩かない）。
const { experientialStayList } = await useExperientialStays(curatedStaysSection);

// ヒーロー画像(LCP候補=先頭スライド)の preload は MainVisualSlider 内の OptimizedImage(:preload) が
// 変換URL・正しい media・fetchpriority=high で <head> に登録する。ここで生パスを別途 preload すると
// 変換URLと一致せず未使用 preload（二重DL・帯域浪費）になるため、画像 preload は置かない。
useHead({
  link: [
    { rel: 'canonical', href: buildUrlWithEndSlash(route.path) },
    // Material Symbols Sharp（Experiential Stays セクションの矢印・時計・chevron アイコンで使用）
    { rel: 'stylesheet', href: ICON_FONT_URL },
  ],
});
</script>
